import React, { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { apiGet, apiPatch, apiPost } from "../services/api";
import { useToast } from "../components/common/ToastProvider";
import ClinicIcon from "../components/common/ClinicIcon";

import NotesAndDiagnosis from "../components/doctor/NotesAndDiagnosis";

import { useClinicStore } from "../store/clinicStore";
import PatientSearch, { type PatientResult } from "../components/doctor/PatientSearch";
import CalendarDayView from "../components/doctor/clinical/CalendarDayView";
import ClinicalWorkspace from "../components/doctor/clinical/ClinicalWorkspace";
import HistoryView from "../components/doctor/clinical/HistoryView";
import ProcedurePage from "../components/doctor/clinical/ProcedurePage";
import PrescriptionPanel from "../components/doctor/PrescriptionPanel";
import DoctorReview from "../components/doctor/review/DoctorReview";
import DoctorModuleHeader from "../components/doctor/DoctorModuleHeader";
import ErrorBoundary from "../components/manager/shared/ErrorBoundary";
import AppShellLayout from "../components/layout/AppShellLayout";
import PerioConsole from "../components/doctor/perio/PerioConsole";
import TreatmentPlanManager from "../components/doctor/treatment_plan/TreatmentPlanManager";
import ActivePatientBar from "../components/common/ActivePatientBar";
import PatientEditModal from "../components/careflow/PatientEditModal";
import { useI18n } from "../i18n/I18nProvider";

import "./Doctor.css";
import BackendRequired from "../components/common/BackendRequired";

interface PatientLike {
  patient_id?: string | number;
  name?: string;
  gender?: string;
  age?: string | number;
  phone?: string;
  mobile?: string;
  [key: string]: unknown;
}

interface VisitLike {
  visit_id?: string | number;
  patient_id?: string | number;
  status?: string;
  visit_date?: string;
  [key: string]: unknown;
}

interface ProcInfo {
  masterId?: string | null;
  tooth?: string | null;
  surface?: string | null;
  diagnosisCode?: string | null;
  [key: string]: unknown;
}

interface DashboardVisit {
  visit_id?: string | number;
  status?: string;
  patient_id?: string | number;
  patient_name?: string;
  gender?: string;
  age?: string | number;
  phone?: string;
  [key: string]: unknown;
}

export interface Consultant {
  id: number;
  name: string;
  role?: string;
  is_active?: boolean;
}

type DoctorTab =
  | "dashboard"
  | "clinical-notes"
  | "toothchart"
  | "treatment-plan"
  | "perio"
  | "history"
  | "prescription"
  | "review";

export default function Doctor() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [patient, setPatient] = useState<PatientLike | null>(null);
  const [visit, setVisit] = useState<VisitLike | null>(null);
  const [activeTab, setActiveTab] = useState<DoctorTab>("clinical-notes");
  const [clinicalView, setClinicalView] = useState<"main" | "procedure">("main");
  const [activeProcedure, setActiveProcedure] = useState<ProcInfo | null>(null);
  const [scheduleDate, setScheduleDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [sessionConsultant, setSessionConsultant] = useState<Consultant | null | undefined>(undefined);
  const [loadingVisit, setLoadingVisit] = useState(false);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const [dashboardVisits, setDashboardVisits] = useState<DashboardVisit[]>([]);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const { showToast } = useToast();

  const setActivePatient = useClinicStore((s) => s.setActivePatient);
  const setActiveVisit = useClinicStore((s) => s.setActiveVisit);
  const setActivePatientProfile = useClinicStore((s) => s.setActivePatientProfile);
  const setActiveVisitRecord = useClinicStore((s) => s.setActiveVisitRecord);
  const resetClinicSession = useClinicStore((s) => s.resetSession);
  const setSelectedTooth = useClinicStore((s) => s.setSelectedTooth);
  const storedPatient = useClinicStore((s) => s.activePatient);
  const hydrateRef = useRef(false);

  const isSessionActive = Boolean(patient && visit);
  const normalizeStatus = (raw?: unknown) => String(raw ?? "").trim().toUpperCase().replace(/\s+/g, "_");
  const isVisitLocked = Boolean(visit && ["LOCKED", "CLOSED"].includes(normalizeStatus(visit.status)));

  const dashboardStats = {
    waiting: dashboardVisits.filter((v) => {
      const s = normalizeStatus(v.status);
      return ["WAITING", "CHECKED_IN", "IN_PROGRESS", "IN_CHAIR", "OPEN", "BOOKED", "CONFIRMED", "RESCHEDULED"].includes(s);
    }).length,
    completed: dashboardVisits.filter((v) => {
      const s = normalizeStatus(v.status);
      return ["COMPLETED", "LOCKED", "CLOSED", "DONE"].includes(s);
    }).length,
  };

  const loadDashboardVisits = useCallback(() => {
    if (!scheduleDate) return;
    setDashboardLoading(true);
    apiGet(`/api/visits-by-date?date=${scheduleDate}`)
      .then((value: unknown) => {
        const data = value as { visits?: DashboardVisit[] };
        setDashboardVisits(Array.isArray(data?.visits) ? data.visits : []);
      })
      .catch(() => setDashboardVisits([]))
      .finally(() => setDashboardLoading(false));
  }, [scheduleDate]);

  useEffect(() => {
    // Always load dashboard stats in background or when dash is active
    loadDashboardVisits();
  }, [scheduleDate, loadDashboardVisits]);

  const handleProcedureCompleted = useCallback(() => {
    setHistoryRefreshKey((k) => k + 1);
  }, []);

  const handleHistoryMutated = useCallback(() => {
    setHistoryRefreshKey((k) => k + 1);
  }, []);

  const resetSession = () => {
    setPatient(null);
    setVisit(null);
    setActiveTab("clinical-notes");
    setClinicalView("main");
    setActiveProcedure(null);
    setLoadingVisit(false);
    setActivePatient(null);
    setActiveVisit(null);
    setSelectedTooth(null);
    setSessionConsultant(undefined);
    resetClinicSession();
  };

  const handlePatientSelect = (patient: PatientResult): void => {
    const p = patient as PatientLike;
    if (!p?.patient_id) return;
    setLoadingVisit(true);
    setPatient(p);
    setActivePatientProfile({
      patient_id: p.patient_id,
      name: p.name,
      age: p.age,
      gender: p.gender,
      mobile: p.mobile,
      phone: p.phone,
    });
    setActivePatient(String(p.patient_id));
    setVisit(null);
    setActiveVisit(null);
    setActiveProcedure(null);
    void (async () => {
      try {
        const today = new Date().toISOString().slice(0, 10);
        let v: VisitLike | null = null;
        try {
          v = await apiGet(`/api/visits/today/${p.patient_id}`) as VisitLike | null;
        } catch (err: unknown) {
          const e = err as { status?: number; message?: string };
          if (e?.status === 404 || (e?.message?.includes && e.message.includes("No visit today"))) {
            v = null;
          } else throw err;
        }
        if (!v) {
          try {
            v = await apiPost(`/api/visits/today/${encodeURIComponent(String(p.patient_id))}`, {
              visit_type: "Walk-in",
              remarks: "Walk-in",
              created_source: "doctor_ui",
            }) as VisitLike;
          } catch (postErr: unknown) {
            const e = postErr as { status?: number; message?: string; body?: { visit_id?: string | number } };
            if (e?.status === 409 || (e?.message && (e.message.includes("race condition") || e.message.includes("already exists")))) {
              const body = e?.body != null && typeof e.body === "object" ? e.body : null;
              const visitIdFrom409 = body?.visit_id;
              if (visitIdFrom409 != null) {
                try {
                  v = await apiGet(`/api/visits/id/${visitIdFrom409}`) as VisitLike;
                } catch (_) {
                  v = null;
                }
              }
              if (!v) {
                try {
                  v = await apiGet(`/api/visits/by-date?patient_id=${encodeURIComponent(String(p.patient_id))}&visit_date=${today}`) as VisitLike;
                } catch (_) {
                  v = null;
                }
              }
              if (!v) {
                try {
                  v = await apiGet(`/api/visits/today/${p.patient_id}`) as VisitLike;
                } catch (getErr: unknown) {
                  const ge = getErr as { status?: number; message?: string };
                  if (ge?.status === 404 || (ge?.message?.includes && ge.message.includes("No visit today"))) {
                    const delay = (): Promise<void> => new Promise<void>((resolve: () => void): void => { setTimeout(resolve, 300); });
                    await delay();
                    v = await apiGet(`/api/visits/today/${p.patient_id}`).catch((): null => null) as VisitLike | null;
                  } else {
                    throw getErr;
                  }
                }
              }
            } else {
              throw postErr;
            }
          }
          if (v && typeof v === "object" && "visit_id" in v) {
            v = { visit_id: v.visit_id, patient_id: v.patient_id ?? p.patient_id, status: v.status ?? "CHECKED_IN", visit_date: v.visit_date ?? today };
          }
        }
        setVisit(v);
        if (v != null) {
          const vid = String((v as VisitLike).visit_id ?? (v as { id?: string | number }).id ?? "");
          setActiveVisit(vid);
          setActiveVisitRecord({
            visit_id: (v as VisitLike).visit_id ?? (v as { id?: string | number }).id,
            status: (v as VisitLike).status,
            visit_date: (v as VisitLike).visit_date,
          });
          if (vid) {
            void apiPost(`/api/doctor/visit/start/${encodeURIComponent(vid)}`, {})
              .then((res: unknown) => {
                const nextStatus = (res as { status?: string } | null)?.status;
                if (nextStatus) setVisit((prev) => (prev ? { ...prev, status: nextStatus } : prev));
              })
              .catch(() => undefined);
          }
        } else {
          setActiveVisit(null);
        }
        setActiveTab("clinical-notes"); // Default start tab
        setClinicalView("main");
        setActiveProcedure(null);
      } catch (err) {
        console.error("Visit init failed", err);
        alert(t("doctor_error_start_session"));
        resetSession();
      } finally {
        setLoadingVisit(false);
      }
    })();
  };

  useEffect(() => {
    if (hydrateRef.current) return;
    if (!storedPatient || patient) return;
    hydrateRef.current = true;
    const incoming: PatientResult = {
      patient_id: storedPatient.patient_id ?? "",
      name: storedPatient.name || "",
      gender: storedPatient.gender,
      age: typeof storedPatient.age === "number" ? storedPatient.age : Number(storedPatient.age || 0) || undefined,
      mobile: storedPatient.mobile || storedPatient.phone,
    };
    if (incoming.patient_id) {
      handlePatientSelect(incoming);
    }
  }, [storedPatient, patient]);

  const handleScheduleVisitSelect = async (v: { visit_id?: string | number; patient_id?: string | number; patient_name?: string; gender?: string; age?: string | number; phone?: string }): Promise<void> => {
    const visitId = Number(v?.visit_id);
    const pid = v?.patient_id != null ? String(v.patient_id) : "";
    if (!visitId || !pid) return;

    setLoadingVisit(true);
    const p: PatientLike = {
      patient_id: pid,
      name: v.patient_name || "",
      gender: v.gender,
      age: v.age,
      phone: pid,
    };
    setPatient(p);
    setActivePatientProfile({
      patient_id: p.patient_id,
      name: p.name,
      age: p.age,
      gender: p.gender,
      phone: p.phone,
    });
    setActivePatient(pid);
    setActiveVisit(String(visitId));
    setSelectedTooth(null);
    setActiveProcedure(null);

    try {
      const visitRow = (await apiGet(`/api/visits/id/${encodeURIComponent(String(visitId))}`)) as VisitLike;
      const mergedVisit: VisitLike = {
        ...(visitRow && typeof visitRow === "object" ? visitRow : {}),
        visit_id: visitId,
        patient_id: pid,
        status: (visitRow as VisitLike)?.status ?? (v as { status?: string })?.status,
        visit_date: (visitRow as VisitLike)?.visit_date ?? (v as { visit_date?: string })?.visit_date,
      };
      setVisit(mergedVisit);
      setActiveVisitRecord({
        visit_id: mergedVisit.visit_id,
        status: mergedVisit.status,
        visit_date: mergedVisit.visit_date,
      });
      setActiveTab("clinical-notes");
      setClinicalView("main");
      setActiveProcedure(null);

      // Best-effort workflow transition (don't block UI if it fails)
      void apiPost(`/api/doctor/visit/start/${visitId}`, {})
        .then((res: unknown) => {
          const nextStatus = (res as { status?: string } | null)?.status;
          if (nextStatus) setVisit((prev) => (prev ? { ...prev, status: nextStatus } : prev));
        })
        .catch(() => undefined);
    } catch (err) {
      console.error("Failed to open scheduled visit", err);
      alert(t("doctor_error_open_visit"));
      resetSession();
    } finally {
      setLoadingVisit(false);
    }
  };

  const handleModifyChart = () => {
    setActiveTab("toothchart");
    setClinicalView("main");
  };

  const handleOpenProcedure = useCallback((procInfo: ProcInfo) => {
    setActiveProcedure(procInfo);
    setClinicalView("procedure");
  }, []);

  const handleBackToClinical = useCallback(() => {
    setClinicalView("main");
    setActiveProcedure(null);
  }, []);

  const handleLockSuccess = () => {
    resetSession();
    navigate("/doctor", { replace: true });
  };

  const handleSavePatient = async (data: any) => {
    if (!patient?.patient_id) return;
    try {
      const updated = await apiPatch(`/api/patients/${encodeURIComponent(String(patient.patient_id))}`, data) as any;
      setPatient(updated);
      showToast({ message: "Patient updated successfully", type: "success" });
    } catch (e) {
      showToast({ message: "Failed to update patient", type: "error" });
      throw e;
    }
  };

  const handleLeftNav = (tab: DoctorTab) => {
    if (!isSessionActive && tab !== "dashboard") return;
    setActiveTab(tab);
    if (tab === "clinical-notes" || tab === "toothchart" || tab === "perio") {
      setClinicalView("main");
    }
  };

  // Render helpers
  const renderDashboard = () => (
    <div className="view-dashboard animate-fade">
      <div className="dashboard-top-bar">
        <div className="dashboard-title-area">
          <h2 className="dashboard-title">{t("doctor_dashboard_title") || "Clinical Dashboard"}</h2>
          <p className="dashboard-subtitle">{scheduleDate === new Date().toISOString().split("T")[0] ? "Today's Schedule" : `Schedule for ${scheduleDate}`}</p>
        </div>
        <div className="dashboard-controls">
          <div className="date-picker-container">
            <ClinicIcon name="calendar" size={16} />
            <input
              type="date"
              value={scheduleDate}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setScheduleDate(e.target.value)}
            />
          </div>
          <BackendRequired showDisabled>
            <button className="btn-ops" onClick={() => navigate("/treatments")}>
              <ClinicIcon name="treatments" size={18} />
              <span>{t("doctor_dashboard_clinical_ops")}</span>
            </button>
          </BackendRequired>
        </div>
      </div>

      <div className="dashboard-grid">
        <div className="dashboard-left-col">
          <div className="stats-marquee">
            <div className="stat-glass-card waiting">
              <div className="stat-icon-blob"><ClinicIcon name="queue" size={24} /></div>
              <div className="stat-info">
                <span className="stat-label">{t("doctor_dashboard_waiting_room")}</span>
                <div className="stat-count">{dashboardLoading ? "…" : dashboardStats.waiting}</div>
              </div>
            </div>
            <div className="stat-glass-card completed">
              <div className="stat-icon-blob"><ClinicIcon name="check" size={24} /></div>
              <div className="stat-info">
                <span className="stat-label">{t("doctor_dashboard_completed")}</span>
                <div className="stat-count">{dashboardLoading ? "…" : dashboardStats.completed}</div>
              </div>
            </div>
          </div>

          <div className="quick-access-card">
            <div className="card-header-iconic">
              <div className="icon-circle"><ClinicIcon name="search" size={20} /></div>
              <div>
                <h3>{t("doctor_dashboard_quick_start")}</h3>
                <p>{t("doctor_dashboard_quick_start_sub")}</p>
              </div>
            </div>
            <div className="search-box-themed">
              <PatientSearch onSelect={handlePatientSelect} />
            </div>
          </div>
        </div>

        <div className="dashboard-right-col">
          <div className="schedule-card-wrapper">
            <CalendarDayView
              selectedDate={scheduleDate}
              visits={dashboardVisits.map((v) => ({ ...v, visit_id: v.visit_id != null ? String(v.visit_id) : undefined }))}
              loading={dashboardLoading}
              onSelectVisit={(v) =>
                void handleScheduleVisitSelect({
                  visit_id: v.visit_id,
                  patient_id: v.patient_id,
                  patient_name: v.patient_name,
                  gender: v.gender,
                  age: v.age,
                  phone: v.phone,
                  ...(v as unknown as { visit_date?: string; status?: string }),
                })
              }
            />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="doctor-page ui-page">
      <AppShellLayout
        moduleId="doctor"
        header={(
          <>
            <DoctorModuleHeader
              activeTab={activeTab}
              onSelectTab={handleLeftNav}
              isSessionActive={isSessionActive}
              patientName={patient?.name}
              visitId={visit?.visit_id}
              onEndSession={resetSession}
            />
            {isSessionActive && (
              <ActivePatientBar
                patient={patient}
                visit={visit}
                loadingVisit={loadingVisit}
                onEndSession={resetSession}
                onEdit={() => setShowEditModal(true)}
              />
            )}
          </>
        )}
        left={undefined}
        leftCollapsible={false}
        main={(
          <div className="doctor-main">
            <div className="content-viewport">
              {!isSessionActive || activeTab === "dashboard" ? (
                renderDashboard()
              ) : (
                <div className="view-clinical animate-slide-up">
                  {/* Tab content area */}
                  <div className="tab-content-area">
                    {loadingVisit ? (
                      <div className="loader-box">
                        <div className="spinner"></div>
                      </div>
                    ) : (
                      <div className="main-content">
                        <ErrorBoundary>
                          {/* Clinical Notes & Diagnosis Tab */}
                          {activeTab === "clinical-notes" && visit && (
                            <div className="clinical-notes-container flush">
                              <NotesAndDiagnosis
                                patientId={patient?.patient_id != null ? String(patient.patient_id) : ""}
                                visitId={visit?.visit_id != null ? String(visit.visit_id) : ""}
                                isLocked={isVisitLocked}
                              />
                            </div>
                          )}

                          {/* Toothchart & Procedures Tab */}
                          {activeTab === "toothchart" && visit && (
                            (clinicalView === "procedure" && activeProcedure) ? (
                              <ProcedurePage
                                masterId={activeProcedure.masterId}
                                patient={patient ? { ...patient, patient_id: String(patient.patient_id) } : null}
                                visit={visit ? { ...visit, visit_id: String(visit.visit_id) } : null}
                                tooth={activeProcedure.tooth}
                                surface={activeProcedure.surface}
                                diagnosisCode={activeProcedure.diagnosisCode}
                                isLocked={isVisitLocked}
                                consultantName={sessionConsultant?.name ?? null}
                                onBack={handleBackToClinical}
                                onComplete={() => handleBackToClinical()}
                              />
                            ) : (
                              <ClinicalWorkspace
                                patient={patient as import("../types/domain").Patient}
                                visit={visit as import("../types/domain").Visit}
                                isLocked={isVisitLocked}
                                onOpenProcedure={handleOpenProcedure}
                                onProcedureCompleted={handleProcedureCompleted}
                                onHistoryMutated={handleHistoryMutated}
                                sessionConsultant={sessionConsultant}
                                setSessionConsultant={setSessionConsultant}
                              />
                            )
                          )}

                          {/* Treatment Plan Tab */}
                          {activeTab === "treatment-plan" && visit && patient && (
                            <div className="doctor-panel-fill">
                              <TreatmentPlanManager
                                patientId={patient.patient_id != null ? String(patient.patient_id) : ""}
                                visitId={visit.visit_id != null ? Number(visit.visit_id) : undefined}
                              />
                            </div>
                          )}

                          {/* Perio Tab */}
                          {activeTab === "perio" && visit && (
                            <PerioConsole
                              visitId={visit.visit_id != null ? String(visit.visit_id) : ""}
                              readOnly={isVisitLocked}
                            />
                          )}

                          {/* History Tab */}
                          {activeTab === "history" && visit && patient && (
                            <HistoryView
                              patientId={patient.patient_id != null ? String(patient.patient_id) : undefined}
                              visitId={visit.visit_id != null ? String(visit.visit_id) : undefined}
                              isLocked={isVisitLocked}
                              refreshKey={historyRefreshKey}
                            />
                          )}

                          {/* Prescription Tab */}
                          {activeTab === "prescription" && visit && patient && (
                            <PrescriptionPanel
                              patientId={patient.patient_id != null ? Number(patient.patient_id) : null}
                              visitId={visit.visit_id != null ? Number(visit.visit_id) : null}
                              isLocked={isVisitLocked}
                            />
                          )}

                          {/* Review & Sign Tab */}
                          {activeTab === "review" && visit && patient && (
                            <DoctorReview
                              patient={patient as import("../types/domain").Patient}
                              visit={visit as import("../types/domain").Visit}
                              onModifyChart={handleModifyChart}
                              onLockSuccess={handleLockSuccess}
                            />
                          )}
                        </ErrorBoundary>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      />
      {patient && (
        <PatientEditModal
          patient={patient as any}
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSave={handleSavePatient}
        />
      )}
    </div >
  );
}
